import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import { ResourceForm, ResourcesGrid } from '../components/ResourceLibrary';
import useSharePointList from '../hooks/useSharePointList';
import usePermissions from '../auth/usePermissions';
import {
  createAgreement,
  deleteAgreement,
  getAgreement,
  listAgreements,
  updateAgreement,
  PROPOSAL_STATUS,
} from '../services/agreements';
import {
  createSalesResource,
  deleteSalesResource,
  listSalesResources,
} from '../services/salesResources';
import {
  listSalesTrackerEvents,
  logSupplierPlantAssignmentEvent,
} from '../services/salesTracker';
import { listPlants } from '../services/plants';
import { listParts } from '../services/parts';
import {
  listSuppliers,
  getSupplierByCode,
  createSupplier,
  updateSupplier,
  withProposalAssignment,
  winProposalAssignment,
  loseProposalAssignment,
} from '../services/suppliers';
import { listTeam } from '../services/team';
import { presetRange } from '../services/billing';
import { saveAgreementPdf } from '../utils/proposalPdf';
import { formatCurrency, formatDate } from '../utils/formatters';
import { displayFullName } from '../utils/teamDisplay';
import AgreementForm from './AgreementForm';

/**
 * Sales landing page. Directors only (route is gated in App.jsx).
 *
 * Two top-level sections:
 *   1. Customer Agreements — DataTable of all generated proposals with a
 *      "Save as PDF" action on each row + a "+ New Agreement" button to
 *      open the AgreementForm.
 *   2. Sales Resources — grid of tiles, each linking out to a downloadable
 *      asset (one-pager, deck, case study). Directors can add or remove
 *      resources from the same page.
 *
 * Kept in a single file because both sections share brand styling and
 * only the Director sees this screen — splitting would add indirection
 * without a readability win.
 */
// Turn an agreement's coverage rows into the proposal "lines" the supplier
// helpers understand. Only rows with a real plantCode can attach to a plant
// assignment, so legacy/free-text-only rows are skipped.
function linesFromAgreement(ag) {
  return (Array.isArray(ag?.coverageRows) ? ag.coverageRows : [])
    .filter((r) => (r.plantCode || '').toString().trim())
    .map((r) => ({
      plantCode: String(r.plantCode).toUpperCase().trim(),
      proposedRate: r.hourlyRateUsd,
      hoursAllocated: r.hoursAllocated,
      hoursUnit: r.hoursUnit || '',
    }));
}

export default function Sales() {
  // canManageProposals gates the proposal pipeline (create / edit / send /
  // won / lost) — Directors plus anyone granted a Sales override. isDirector
  // still gates the Director-only Sales Resource upload library below.
  const { isDirector, canManageProposals, name, email } = usePermissions();
  const navigate = useNavigate();

  // ---------- Agreements ----------
  const agreements = useSharePointList(listAgreements);
  // Suppliers — declared up here (not with the other list reads further
  // down) because the proposal save/delete/send handlers above reference
  // it; a later declaration would put it in the temporal dead zone for
  // those useCallback dependency arrays.
  const suppliers = useSharePointList(listSuppliers);

  // Single modal handles both "create new" and "edit existing":
  //   - creating = true + editing = null        → New Agreement flow
  //   - editing != null (full decoded row)      → Edit flow
  //   - both falsy                              → modal closed
  // `saving` blocks the Cancel/close escape so a mid-save dismissal
  // can't orphan the Graph call.
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [agreementError, setAgreementError] = useState('');

  // Year-group collapse state. We don't store "which years are open" —
  // we store "which years has the user flipped from their default." That
  // way the newest year is always expanded on first load (default = open)
  // and older years are collapsed (default = closed), and the set only
  // needs an entry when the user clicks a header. Avoids an initial-state
  // dance tied to data-load timing.
  const [toggledYears, setToggledYears] = useState(() => new Set());
  const toggleYear = (year) =>
    setToggledYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });

  const onNewAgreement = () => {
    setAgreementError('');
    setEditing(null);
    setCreating(true);
  };

  // Row click opens the form in edit mode. We refetch the full record so
  // the editor sees the decoded supportBullets / coverageRows — the list
  // payload already carries them today, but the refetch keeps this safe
  // if we ever switch to a lighter summary shape on the index call.
  const onEditAgreement = async (row) => {
    setAgreementError('');
    try {
      const full = row?.id ? await getAgreement(row.id) : row;
      setCreating(false);
      setEditing(full);
    } catch (err) {
      setAgreementError(
        `Couldn't open that proposal for editing: ${err.message}`,
      );
    }
  };

  const closeAgreementModal = () => {
    // Save/delete in flight — let the operation finish so we don't
    // leave the table out of sync with what the user just committed.
    if (saving || deleting) return;
    setCreating(false);
    setEditing(null);
  };

  const onSaveAgreement = useCallback(
    async (model) => {
      setSaving(true);
      try {
        if (editing?.id) {
          // Edit path: keep the original PostedBy/PostedAt (who originally
          // created the proposal) but stamp LastEditedBy / LastEditedAt
          // so the audit trail shows the latest author. These columns are
          // optional on the SharePoint schema — if they don't exist, the
          // patch quietly drops them.
          await updateAgreement(editing.id, {
            ...model,
            LastEditedBy: name || email || '',
            LastEditedAt: new Date().toISOString(),
          });
          // Re-sync supplier coverage when editing a proposal that's
          // already out (decision #6: edits re-sync the assignment). Best
          // effort — the agreement save already succeeded, so a re-sync
          // hiccup shouldn't surface as a save failure.
          if (
            editing.ProposalStatus === PROPOSAL_STATUS.PROPOSAL_OUT &&
            (model.SupplierCode || '').toString().trim()
          ) {
            try {
              let supplier = await getSupplierByCode(
                String(model.SupplierCode).trim(),
              );
              if (supplier) {
                const events = [];
                for (const line of linesFromAgreement(model)) {
                  const { plantAssignments, event } = withProposalAssignment(
                    supplier,
                    line,
                  );
                  supplier = { ...supplier, plantAssignments };
                  events.push(event);
                }
                await updateSupplier(supplier.id, supplier);
                const postedBy = name || email || '';
                for (const ev of events) {
                  if (!ev) continue;
                  // eslint-disable-next-line no-await-in-loop
                  await logSupplierPlantAssignmentEvent({ ...ev, postedBy });
                }
                await suppliers.refresh();
              }
            } catch (syncErr) {
              // eslint-disable-next-line no-console
              console.warn('[Sales] proposal re-sync failed', syncErr);
            }
          }
        } else {
          await createAgreement({
            ...model,
            ProposalStatus: PROPOSAL_STATUS.DRAFT,
            PostedBy: name || email || '',
            PostedAt: new Date().toISOString(),
          });
        }
        setCreating(false);
        setEditing(null);
        await agreements.refresh();
      } finally {
        setSaving(false);
      }
    },
    [agreements, suppliers, editing, name, email],
  );

  // Remove an agreement from SharePoint. Plain window.confirm matches
  // the delete pattern used elsewhere (ResourceTile, Parts) — only
  // Directors reach this button and a dedicated confirmation modal is
  // overkill for a deliberate click. We surface errors inline on the
  // Sales page so the modal can still close.
  const onDeleteAgreement = useCallback(async () => {
    if (!editing?.id) return;
    const label = editing.CustomerCompany
      ? `the proposal for ${editing.CustomerCompany}`
      : 'this proposal';
    const wasOut =
      editing.ProposalStatus === PROPOSAL_STATUS.PROPOSAL_OUT &&
      (editing.SupplierCode || '').toString().trim();
    const confirmMsg = wasOut
      ? `Delete ${label}? This can't be undone. The customer's "Proposal Out" coverage will be kept (and flagged), not removed — change it on their page if needed.`
      : `Delete ${label}? This can't be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      await deleteAgreement(editing.id);
      // Decision #6: a deleted proposal LEAVES the supplier's coverage in
      // place but flags it, so nothing silently disappears. Append a dated
      // note to each plant assignment the proposal touched. Best effort.
      if (wasOut) {
        try {
          let supplier = await getSupplierByCode(
            String(editing.SupplierCode).trim(),
          );
          if (supplier) {
            const codes = new Set(
              linesFromAgreement(editing).map((l) => l.plantCode),
            );
            const stamp = new Date().toISOString().slice(0, 10);
            const flag = `[Proposal deleted ${stamp}]`;
            const plantAssignments = (supplier.plantAssignments || []).map(
              (pa) => {
                const code = String(pa.plantCode || '').toUpperCase().trim();
                if (
                  !codes.has(code) ||
                  pa.coverageStatus !== 'Proposal Out' ||
                  (pa.notes || '').includes(flag)
                ) {
                  return pa;
                }
                return {
                  ...pa,
                  notes: [pa.notes, flag].filter(Boolean).join(' ').trim(),
                };
              },
            );
            await updateSupplier(supplier.id, { ...supplier, plantAssignments });
            await suppliers.refresh();
          }
        } catch (flagErr) {
          // eslint-disable-next-line no-console
          console.warn('[Sales] proposal delete-flag failed', flagErr);
        }
      }
      setEditing(null);
      await agreements.refresh();
    } catch (err) {
      setAgreementError(`Couldn't delete the proposal: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  }, [agreements, suppliers, editing]);

  // Download a proposal PDF. We refetch the full record by id so the PDF
  // builder sees the decoded supportBullets / coverageRows even if the
  // DataTable row was a summary shape.
  const onDownloadAgreement = async (row) => {
    try {
      const full = row.id ? await getAgreement(row.id) : row;
      await saveAgreementPdf(full);
    } catch (err) {
      // Fall back to the row we already have — better to download a
      // partially-populated PDF than nothing.
      console.error('[Sales] PDF download failed on refetch, using row', err);
      await saveAgreementPdf(row);
    }
  };

  // The proposal-pipeline action handlers (Send / Won / Lost) are defined
  // further down, after the suppliers list is loaded. The agreementColumns
  // memo (deps: []) reads them through this ref at render time so the memo
  // never closes over a stale handler and we avoid a temporal-dead-zone
  // reference to consts declared below.
  const proposalActionsRef = useRef({});

  const agreementColumns = useMemo(
    () => [
      {
        // "Date Created" = when the agreement was saved into the portal.
        // PostedAt is what Sales.jsx stamps on create, with a fallback to
        // SharePoint's standard Created field for any row that predates
        // that stamp (older agreements, or rows edited outside the portal).
        key: 'PostedAt',
        label: 'Date Created',
        render: (r) => formatDate(r.PostedAt || r.Created),
      },
      { key: 'CustomerCompany', label: 'Customer' },
      { key: 'CustomerContactName', label: 'Contact' },
      {
        // Plant column — coverageRows is an array because an agreement can
        // cover multiple plants. Join the unique, non-empty plant labels so
        // the cell reads "Plant A, Plant B" without duplicating entries if
        // the same plant appears on two coverage rows (different shifts).
        key: '_plant',
        label: 'Plant',
        render: (r) => {
          const rows = Array.isArray(r.coverageRows) ? r.coverageRows : [];
          const plants = Array.from(
            new Set(
              rows
                .map((row) => (row?.plant || '').trim())
                .filter(Boolean),
            ),
          );
          return plants.join(', ');
        },
      },
      {
        // Hourly Rate — from the same coverageRows array. Formatted as
        // USD currency (same formatter used inside the PDF table) so
        // "$35.00" reads consistently across both views. If multiple
        // coverage rows use different rates, join the unique values with
        // commas; a single rate shows once.
        key: '_hourlyRate',
        label: 'Hourly Rate',
        filter: false,
        sortable: false,
        render: (r) => {
          const rows = Array.isArray(r.coverageRows) ? r.coverageRows : [];
          const rates = Array.from(
            new Set(
              rows
                .map((row) =>
                  row?.hourlyRateUsd != null && row.hourlyRateUsd !== ''
                    ? formatCurrency(row.hourlyRateUsd)
                    : '',
                )
                .filter(Boolean),
            ),
          );
          return rates.join(', ');
        },
      },
      {
        // Coverage Amount — the free-text "coverage per production day"
        // field on each coverage row ("2 Hours", "Full shift", etc.).
        // Same join strategy as Plant and Hourly Rate.
        key: '_coverage',
        label: 'Coverage Amount',
        filter: false,
        sortable: false,
        render: (r) => {
          const rows = Array.isArray(r.coverageRows) ? r.coverageRows : [];
          const coverages = Array.from(
            new Set(
              rows
                .map((row) => (row?.coverage || '').trim())
                .filter(Boolean),
            ),
          );
          return coverages.join(', ');
        },
      },
      {
        // "Owner" in the table header, backed by the form's PreparedByName
        // field (the Proxemics rep who authored the proposal). Sits at the
        // end of the data columns so the table reads left-to-right as
        // "what / who it's for → who owns it internally".
        key: 'PreparedByName',
        label: 'Owner',
      },
      {
        key: '_actions',
        label: '',
        filter: false,
        render: (r) => {
          // Handlers + capability come through the ref so this memoized
          // column always calls the current versions (see proposalActionsRef).
          const acts = proposalActionsRef.current || {};
          const status = r.ProposalStatus || PROPOSAL_STATUS.DRAFT;
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {acts.canManage && status === PROPOSAL_STATUS.DRAFT && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    acts.onSendProposal?.(r);
                  }}
                  title="Push this proposal's coverage onto the customer at Proposal Out"
                >
                  Send / Mark Proposal Out
                </button>
              )}
              {acts.canManage && status === PROPOSAL_STATUS.PROPOSAL_OUT && (
                <>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      acts.onWonProposal?.(r);
                    }}
                    title="Mark won — coverage goes Active and starts counting"
                  >
                    Won
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      acts.onLostProposal?.(r);
                    }}
                    title="Mark lost — coverage is closed"
                    style={{
                      color: 'var(--brand-danger)',
                      borderColor: 'var(--brand-danger)',
                    }}
                  >
                    Lost
                  </button>
                </>
              )}
              {status === PROPOSAL_STATUS.WON && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--brand-primary)',
                    alignSelf: 'center',
                  }}
                >
                  ✓ Won
                </span>
              )}
              {status === PROPOSAL_STATUS.LOST && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--brand-muted)',
                    alignSelf: 'center',
                  }}
                >
                  Lost
                </span>
              )}
              <button
                className="btn btn-outline btn-sm"
                onClick={(e) => {
                  // The row's onRowClick is no-op here, but keep the stop
                  // for future-proofing if we add a detail-view route.
                  e.stopPropagation();
                  onDownloadAgreement(r);
                }}
                title="Save as PDF"
              >
                Save as PDF
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  // Group agreements by year so the proposal list stays manageable as it
  // grows. Year comes from PostedAt (when the row was saved in the portal)
  // with a fallback to SharePoint's Created column — same precedence the
  // "Date Created" column uses, so grouping and the displayed column
  // always agree. Rows with no parseable date land in "Undated" (edge
  // case, but would otherwise look like year 1970).
  const agreementGroups = useMemo(() => {
    const rows = agreements.data || [];
    const groups = new Map();
    for (const r of rows) {
      const raw = r.PostedAt || r.Created || r.ProposalDate;
      const d = raw ? new Date(raw) : null;
      const year =
        d && !Number.isNaN(d.getTime()) ? String(d.getFullYear()) : 'Undated';
      if (!groups.has(year)) groups.set(year, []);
      groups.get(year).push(r);
    }
    // Sort years newest-first; push "Undated" (if any) to the bottom.
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === 'Undated') return 1;
      if (b[0] === 'Undated') return -1;
      return Number(b[0]) - Number(a[0]);
    });
  }, [agreements.data]);

  // Newest group (index 0) defaults to expanded; everything else defaults
  // collapsed. toggledYears is the set of years where the user has
  // flipped from the default, so the check is a simple XOR.
  const isYearExpanded = (year, index) => {
    const defaultOpen = index === 0;
    return toggledYears.has(year) ? !defaultOpen : defaultOpen;
  };

  // ---------- Sales Tracker ----------
  // Append-only log of coverage-hour changes on Parts. Each row is either
  // an "Added" event (a brand-new part with non-zero HoursOfCoverage) or a
  // "Revised" event (a part edit that moved the value up or down). The
  // Sales page sums HoursAdded / HoursLost for the headline totals and
  // lists individual events below.
  const trackerEvents = useSharePointList(listSalesTrackerEvents);
  // Plants power two tracker columns — "Customer" (Plant → OEM) and
  // "Plant" (PlantCode → plant Title). We resolve at render time rather
  // than denormalize onto the event row so a plant rename / OEM change
  // flows through without having to rewrite history.
  const plants = useSharePointList(listPlants);
  // Parts power the orphan filter below — tracker events whose PartCode
  // doesn't match a currently-existing Part are hidden from the Sales
  // Tracker, so a deleted part's history never shows up here. Jillian's
  // rule is "Delete = mistake, wipe history" and "Status = Closed keeps
  // history" — this filter is the UI half of that contract.
  const parts = useSharePointList(listParts);
  // Team roster used to be loaded only by the Pipeline section, but
  // the Sales Tracker filter card now needs it for the Regional
  // Manager dropdown — so the fetch is pulled up here to ride along
  // with the other top-of-page list reads.
  const teamRoster = useSharePointList(listTeam);
  const plantByCode = useMemo(() => {
    const map = {};
    for (const p of plants.data || []) {
      if (p.PlantCode) map[p.PlantCode] = p;
    }
    return map;
  }, [plants.data]);
  // PartCode → SupplierCode lookup, used by the Sales Tracker
  // supplier filter to resolve a part-coverage event (which stores
  // only PartCode) back to its supplier. Plant-assignment events
  // already carry SupplierCode directly so they don't need this.
  const partSupplierByCode = useMemo(() => {
    const map = {};
    for (const p of parts.data || []) {
      const pc = (p.PartCode || '').trim();
      if (!pc) continue;
      map[pc] = String(p.SupplierCode || '').toUpperCase();
    }
    return map;
  }, [parts.data]);
  // {PLANTCODE → [Regional Manager team-records]} lookup, originally
  // built for the Prospecting Suppliers pipeline table; now also used
  // by the Sales Tracker filter to drop tracker events that don't
  // have any RM matching the dropdown selection. Filters to
  // currently-working members (Active or On Leave).
  const rmByPlantCode = useMemo(() => {
    const map = {};
    const ACTIVE = new Set(['Active', 'On Leave']);
    for (const m of teamRoster.data || []) {
      if (!ACTIVE.has(m.EmploymentStatus)) continue;
      if (m.PortalRole !== 'RegionalManager') continue;
      const codes = Array.isArray(m.plantCodes) ? m.plantCodes : [];
      for (const code of codes) {
        const upper = String(code || '').toUpperCase();
        if (!upper) continue;
        if (!map[upper]) map[upper] = [];
        map[upper].push(m);
      }
    }
    return map;
  }, [teamRoster.data]);
  // Set of PartCodes that currently exist in the Parts list. `null` while
  // Parts is still loading — the filter below treats `null` as "don't
  // filter yet" so we don't briefly hide real events during page load.
  const existingPartCodes = useMemo(() => {
    if (!parts.data) return null;
    const set = new Set();
    for (const p of parts.data) {
      const code = (p.PartCode || '').trim();
      if (code) set.add(code);
    }
    return set;
  }, [parts.data]);
  // Tracker events filtered to drop any whose PartCode no longer matches
  // a live Part. Events with an empty PartCode pass through (legacy rows
  // and anything logged without a code — we can't orphan-check them).
  // Plant Assignment events surface only when the logger recorded a
  // non-zero HoursAdded / HoursLost. The logger already gates that on
  // Active state — Active hours going up or down (including a status
  // flip into or out of Active, or a brand-new Active pair, or removing
  // an Active pair) — so any rate-only / status-only edits, plus any
  // edits to a non-Active assignment, naturally drop out here.
  const visibleTrackerEvents = useMemo(() => {
    const rows = trackerEvents.data || [];
    return rows.filter((r) => {
      if ((r.Category || '') === 'Plant Assignment') {
        const added = Number(r.HoursAdded) || 0;
        const lost = Number(r.HoursLost) || 0;
        return added > 0 || lost > 0;
      }
      // Hide events whose Category isn't a coverage event at all
      // (e.g. Contact additions go to the Dashboard's Recent Activity,
      // not here). PartCode-bearing events with no Category set are
      // treated as part-coverage events for back-compat.
      if ((r.Category || '') === 'Contact') return false;
      if (!existingPartCodes) return true;
      const code = (r.PartCode || '').trim();
      if (!code) return true;
      return existingPartCodes.has(code);
    });
  }, [trackerEvents.data, existingPartCodes]);
  // ---------- Sales Tracker filters ----------
  // Date range + Plant + Supplier + Regional Manager — the same filter
  // shape the Billing dashboard uses. Defaults to Year-to-Date on
  // first load so the headline numbers and Activity table read like
  // the previous "current year" scope, but the Director can pick any
  // window or narrow by plant / supplier / RM and every tile +
  // Activity row updates live downstream.
  const defaultRange = useMemo(() => presetRange('ytd'), []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [activePreset, setActivePreset] = useState('ytd');
  const [plantFilter, setPlantFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const applyPreset = (preset) => {
    const { startDate: s, endDate: e } = presetRange(preset);
    setStartDate(s);
    setEndDate(e);
    setActivePreset(preset);
  };

  // Tracker events after the user's filter selections are applied.
  // Replaces the old currentYearEvents memo — same set of rows feeds
  // the headline tiles and the Activity DataTable. Filters stack:
  // every predicate must pass for the row to survive.
  const filteredTrackerEvents = useMemo(() => {
    const plantTarget = plantFilter ? plantFilter.toUpperCase() : '';
    const supplierTarget = supplierFilter ? supplierFilter.toUpperCase() : '';
    return visibleTrackerEvents.filter((r) => {
      // Date range — bounds are inclusive on both ends. Use an ISO
      // YYYY-MM-DD slice from the PostedAt timestamp so the
      // comparison matches the <input type="date"> string literally
      // and we don't get timezone surprises around midnight.
      const raw = r.PostedAt || r.Created;
      if (!raw) return false;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return false;
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (startDate && iso < startDate) return false;
      if (endDate && iso > endDate) return false;
      // Plant — PlantCode field may be a comma-joined list (a part
      // that ships to multiple plants). The row passes if ANY of its
      // codes matches the selected plant.
      if (plantTarget) {
        const codes = (r.PlantCode || '')
          .split(',')
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean);
        if (!codes.includes(plantTarget)) return false;
      }
      // Supplier — plant-assignment events store SupplierCode
      // directly; part-coverage events store only PartCode, so we
      // resolve to the part's supplier via partSupplierByCode.
      if (supplierTarget) {
        let supCode = '';
        if ((r.Category || '') === 'Plant Assignment') {
          supCode = String(r.SupplierCode || '').toUpperCase();
        } else {
          supCode = partSupplierByCode[(r.PartCode || '').trim()] || '';
        }
        if (supCode !== supplierTarget) return false;
      }
      return true;
    });
  }, [
    visibleTrackerEvents,
    startDate,
    endDate,
    plantFilter,
    supplierFilter,
    partSupplierByCode,
  ]);
  // PlantCode on an event row may be a comma-joined list of codes (a part
  // that ships to multiple plants). Split + resolve each + de-duplicate so
  // multi-plant events read cleanly.
  const resolvePlantField = (event, field) => {
    const raw = (event.PlantCode || '').trim();
    if (!raw) return '';
    const codes = raw
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    const values = Array.from(
      new Set(
        codes
          .map((code) => {
            const p = plantByCode[code];
            if (field === 'Title') return p?.Title || code;
            if (field === 'OEM') return p?.OEM || '';
            return '';
          })
          .filter(Boolean),
      ),
    );
    return values.join(', ');
  };
  const trackerTotals = useMemo(() => {
    // Totals roll up from filteredTrackerEvents — date-window
    // scoped + orphan-free + narrowed by the Plant / Supplier / RM
    // dropdowns. Out-of-window events and events that don't pass the
    // user's filter selections don't contribute to the headline
    // Hours Added / Hours Lost numbers.
    let added = 0;
    let lost = 0;
    for (const r of filteredTrackerEvents) {
      added += Number(r.HoursAdded) || 0;
      lost += Number(r.HoursLost) || 0;
    }
    return { added, lost, net: added - lost, count: filteredTrackerEvents.length };
  }, [filteredTrackerEvents]);
  const trackerColumns = useMemo(
    () => [
      {
        key: 'PostedAt',
        label: 'Date',
        render: (r) => formatDate(r.PostedAt || r.Created),
      },
      {
        // Customer = OEM of the plant(s) the part ships to ("Stellantis",
        // "Ford", etc.). Resolved from the Plants list so a renamed OEM
        // propagates without a backfill.
        key: '_customer',
        label: 'Customer',
        sortValue: (r) => resolvePlantField(r, 'OEM'),
        render: (r) => resolvePlantField(r, 'OEM') || '—',
      },
      {
        // Plant = the human-readable plant Title (e.g. "Sterling Heights
        // Assembly") rather than the raw code that's stored on the event.
        key: '_plant',
        label: 'Plant',
        sortValue: (r) => resolvePlantField(r, 'Title'),
        render: (r) => resolvePlantField(r, 'Title') || '—',
      },
      {
        // Single "Item" column that shows whichever name applies for
        // the row's category — Part name for part-coverage events,
        // Supplier name for plant-assignment events. Both event types
        // stash their display label in PartTitle (the salesTracker
        // service reuses that column for the supplier name on
        // plant-assignment rows so we don't need a second column or
        // a second list read here).
        key: 'PartTitle',
        label: 'Part / Supplier',
        render: (r) => {
          const label = (r.PartTitle || '').trim();
          if (label) return label;
          // Fall back to the code if the denormalized name is blank —
          // can happen on older rows logged before we started stamping
          // PartTitle for plant-assignment events.
          if ((r.Category || '') === 'Plant Assignment') {
            return r.SupplierCode || '—';
          }
          return r.PartCode || '—';
        },
      },
      {
        key: 'HoursAdded',
        label: 'Hours Added',
        filter: false,
        sortValue: (r) => Number(r.HoursAdded) || 0,
        render: (r) => {
          const n = Number(r.HoursAdded) || 0;
          return n > 0 ? (
            <span style={{ color: 'var(--brand-success, #16a34a)', fontWeight: 500 }}>
              +{n}h
            </span>
          ) : (
            '—'
          );
        },
      },
      {
        key: 'HoursLost',
        label: 'Hours Lost',
        filter: false,
        sortValue: (r) => Number(r.HoursLost) || 0,
        render: (r) => {
          const n = Number(r.HoursLost) || 0;
          return n > 0 ? (
            <span style={{ color: 'var(--brand-danger)', fontWeight: 500 }}>
              −{n}h
            </span>
          ) : (
            '—'
          );
        },
      },
    ],
    [plantByCode],
  );

  // ---------- Pipeline (Pre-Launch Plants + Prospecting Suppliers) ----------
  // Two upcoming-pipeline tables, both built from existing data sources
  // — no new SharePoint columns. Pre-Launch comes from the per-vehicle
  // status on each plant; Prospecting comes from the per-plant
  // coverageStatus on each supplier's plantAssignments. Regional
  // Manager is resolved from the team roster (declared above for the
  // Sales Tracker filter card) the same way the Plants index does.

  // Pre-launch lookup: a plant counts as Pre-Launch if any of its
  // vehicles is in Pre-Launch AND no vehicle is in a more-active stage
  // (Launch / Production). Mirrors the effectivePlantStatus logic on
  // the Plants index so the two pages agree.
  const prelaunchPlants = useMemo(() => {
    const list = plants.data || [];
    return list
      .filter((p) => {
        const vehicles = Array.isArray(p.vehicles) ? p.vehicles : [];
        let bestPriority = -Infinity;
        let hasPreLaunch = false;
        for (const v of vehicles) {
          const status = (v?.status || '').trim();
          if (!status) continue;
          if (status === 'Pre-Launch') hasPreLaunch = true;
          // Same priority table as Plants.jsx — keep in sync.
          const priority = {
            'Pre-Launch': 1,
            Launch: 2,
            Production: 3,
            'Wind-Down': 0,
            Closed: -1,
          }[status];
          if (priority != null && priority > bestPriority) {
            bestPriority = priority;
          }
        }
        return hasPreLaunch && bestPriority === 1;
      })
      .sort((a, b) =>
        (a.Title || '').localeCompare(b.Title || ''),
      );
  }, [plants.data]);

  // (rmByPlantCode was lifted to the top of the component so the
  // Sales Tracker filter card can share it with the Prospecting /
  // Proposal Out tables below — same lookup, same source.)

  // Flatten supplier.plantAssignments into one row per (supplier,
  // plant) pair, keeping only the assignments whose coverageStatus
  // matches the requested status. Powers the Prospecting Suppliers
  // and Proposal Out Suppliers tables. A supplier with two matching
  // plants shows as two rows.
  const buildSupplierStatusRows = useCallback(
    (status) => {
      const out = [];
      for (const s of suppliers.data || []) {
        const assignments = Array.isArray(s.plantAssignments)
          ? s.plantAssignments
          : [];
        for (const pa of assignments) {
          if ((pa.coverageStatus || '') !== status) continue;
          const plantCode = String(pa.plantCode || '').toUpperCase();
          const plant = plantCode ? plantByCode[plantCode] : null;
          const rms = (rmByPlantCode[plantCode] || [])
            .map(displayFullName)
            .filter(Boolean);
          out.push({
            // Stable key for React — supplier.id + plantCode
            id: `${s.id}-${plantCode}`,
            supplierId: s.id,
            supplierCode: s.SupplierCode,
            supplierName: s.Title || s.SupplierCode || '—',
            plantCode,
            plantName: plant?.Title || plantCode || '—',
            hourlyRate:
              pa.hourlyRate === '' || pa.hourlyRate == null
                ? null
                : Number(pa.hourlyRate),
            coverageHours:
              pa.hoursAllocated === '' || pa.hoursAllocated == null
                ? null
                : Number(pa.hoursAllocated),
            coverageUnit:
              pa.hoursUnit === 'Other'
                ? (pa.hoursUnitOther || 'Other').trim()
                : pa.hoursUnit || '',
            regionalManagers: rms,
            lastUpdated: s.Modified || s.Created || '',
            // Per-plant notes — surfaced as a sub-row under the
            // main row when present so directors can read context
            // without opening the supplier page.
            notes: (pa.notes || '').trim(),
          });
        }
      }
      return out.sort((a, b) =>
        (a.supplierName || '').localeCompare(b.supplierName || ''),
      );
    },
    [suppliers.data, plantByCode, rmByPlantCode],
  );

  const prospectingRows = useMemo(
    () => buildSupplierStatusRows('Prospecting'),
    [buildSupplierStatusRows],
  );
  const proposalOutRows = useMemo(
    () => buildSupplierStatusRows('Proposal Out'),
    [buildSupplierStatusRows],
  );

  // Column configs for the two pipeline tables. Pre-launch shows the
  // plant identity + when the plant page was last edited. Prospecting
  // unfolds each supplier's prospecting plant assignments into their
  // own row with the per-plant rate / coverage figures.
  const prelaunchColumns = useMemo(
    () => [
      { key: 'Title', label: 'Plant Name' },
      {
        key: 'PlantCode',
        label: 'Code',
        render: (r) =>
          r.PlantCode ? (
            <span className="badge plant">{r.PlantCode}</span>
          ) : (
            <span className="muted">—</span>
          ),
      },
      { key: 'OEM', label: 'OEM' },
      {
        // Vehicle and Program columns are scoped to the Pre-Launch
        // vehicles only — a plant in our Pre-Launch list might also
        // have an unrelated Production vehicle, but the upcoming
        // pipeline only cares about what's still launching. Multi-
        // vehicle plants render their entries comma-joined.
        key: '_vehicle',
        label: 'Vehicle',
        sortValue: (r) => prelaunchVehicleLabels(r).join(', '),
        render: (r) => {
          const labels = prelaunchVehicleLabels(r);
          return labels.length === 0 ? (
            <span className="muted">—</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {labels.map((label) => (
                <span key={label} className="badge vehicle">
                  {label}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: '_program',
        label: 'Program',
        sortValue: (r) => prelaunchProgramLabels(r).join(', '),
        render: (r) => {
          const labels = prelaunchProgramLabels(r);
          return labels.length === 0 ? (
            <span className="muted">—</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {labels.map((label) => (
                <span key={label} className="badge program">
                  {label}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: 'Modified',
        label: 'Last Updated',
        sortValue: (r) => new Date(r.Modified || r.Created || 0).getTime(),
        render: (r) => {
          const v = r.Modified || r.Created;
          return v ? formatDate(v) : <span className="muted">—</span>;
        },
      },
    ],
    [],
  );

  const prospectingColumns = useMemo(
    () => [
      {
        key: 'supplierName',
        label: 'Supplier',
        render: (r) => (
          <Link
            to={`/suppliers/${encodeURIComponent(r.supplierCode || '')}`}
            style={{ color: 'var(--brand-ink)' }}
          >
            {r.supplierName}
          </Link>
        ),
      },
      {
        key: 'plantCode',
        label: 'Plant',
        render: (r) =>
          r.plantCode ? (
            <Link
              to={`/plants/${encodeURIComponent(r.plantCode)}`}
              style={{ textDecoration: 'none' }}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="badge plant">{r.plantCode}</span>
            </Link>
          ) : (
            <span className="muted">—</span>
          ),
      },
      {
        key: 'coverageHours',
        label: 'Coverage Hours',
        sortValue: (r) => Number(r.coverageHours) || 0,
        render: (r) =>
          r.coverageHours == null ? (
            <span className="muted">—</span>
          ) : (
            r.coverageHours
          ),
      },
      {
        key: 'coverageUnit',
        label: 'Coverage Unit',
        render: (r) => r.coverageUnit || <span className="muted">—</span>,
      },
      {
        key: 'hourlyRate',
        label: 'Hourly Rate',
        sortValue: (r) => Number(r.hourlyRate) || 0,
        render: (r) =>
          r.hourlyRate == null ? (
            <span className="muted">—</span>
          ) : (
            `$${r.hourlyRate.toFixed(2)}/hr`
          ),
      },
      {
        key: '_rm',
        label: 'Regional Manager',
        sortValue: (r) => (r.regionalManagers || []).join(', '),
        render: (r) =>
          r.regionalManagers.length === 0 ? (
            <span className="muted">—</span>
          ) : (
            r.regionalManagers.join(', ')
          ),
      },
      {
        key: 'lastUpdated',
        label: 'Last Updated',
        sortValue: (r) => new Date(r.lastUpdated || 0).getTime(),
        render: (r) =>
          r.lastUpdated ? (
            formatDate(r.lastUpdated)
          ) : (
            <span className="muted">—</span>
          ),
      },
    ],
    [],
  );

  // ---------- Sales Resources ----------
  const resources = useSharePointList(listSalesResources);
  const [addingResource, setAddingResource] = useState(false);
  const [resourceSaving, setResourceSaving] = useState(false);
  // Surface upload errors inside the modal instead of swallowing them.
  // Uploads can fail for a handful of reasons (library not provisioned,
  // no write access, file too large for the tenant, transient network)
  // and Jillian needs to see the actual message to report it back.
  const [resourceError, setResourceError] = useState('');

  const onAddResource = () => {
    setResourceError('');
    setAddingResource(true);
  };

  const onSaveResource = useCallback(
    async (draft) => {
      setResourceSaving(true);
      setResourceError('');
      try {
        await createSalesResource({
          file: draft.file,
          Title: draft.Title,
          Description: draft.Description,
          Category: draft.Category,
          PostedBy: name || email || '',
          PostedAt: new Date().toISOString(),
        });
        setAddingResource(false);
        await resources.refresh();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[Sales] Upload failed:', err);
        setResourceError(
          err?.message ||
            'Upload failed for an unknown reason. Check the browser console for details.',
        );
      } finally {
        setResourceSaving(false);
      }
    },
    [resources, name, email],
  );

  const onRemoveResource = async (row) => {
    // Plain browser confirm — same pattern used elsewhere in the portal
    // for low-stakes delete actions. A dedicated confirm modal is overkill
    // here since the user is a Director making a deliberate click.
    if (!window.confirm(`Remove “${row.Title}” from Sales Resources?`)) return;
    await deleteSalesResource(row.id);
    await resources.refresh();
  };

  // ---------- Proposal pipeline (Send / Won / Lost) ----------
  // Defined here (after suppliers/plants are loaded) and exposed to the
  // memoized agreement table via proposalActionsRef. Each handler mutates
  // the linked supplier's plant coverage, logs a Sales Tracker event per
  // plant, then flips the agreement's ProposalStatus.

  // Smallest unused integer ≥ 100, zero-padded to 3 digits — mirrors the
  // SupplierForm "next available code" suggestion so codes stay consistent.
  const nextSupplierCode = useCallback(() => {
    const taken = new Set();
    for (const s of suppliers.data || []) {
      const n = parseInt(String(s.SupplierCode), 10);
      if (Number.isFinite(n) && n > 0) taken.add(n);
    }
    let c = 100;
    while (taken.has(c)) c += 1;
    return String(c).padStart(3, '0');
  }, [suppliers.data]);

  const onSendProposal = useCallback(
    async (row) => {
      try {
        const ag = row?.id ? await getAgreement(row.id) : row;
        const lines = linesFromAgreement(ag);
        if (lines.length === 0) {
          setAgreementError(
            'This proposal has no coverage rows with a plant selected, so there is nothing to send. Edit it and pick a plant on each coverage row.',
          );
          return;
        }
        if (
          !window.confirm(
            `Send this proposal to ${ag.CustomerCompany || 'the customer'}? This sets their coverage to "Proposal Out".`,
          )
        ) {
          return;
        }

        let supplier;
        let isNew = false;
        const code = (ag.SupplierCode || '').toString().trim();
        if (code) supplier = await getSupplierByCode(code);
        if (!supplier) {
          // New customer — build a minimal supplier record from the
          // proposal's Prepared-For block. Reporting cadence, PO terms,
          // etc. get filled in later (e.g. when the deal is won).
          isNew = true;
          supplier = {
            Title: ag.CustomerCompany || 'New Customer',
            SupplierCode: nextSupplierCode(),
            AddressStreet: ag.CustomerStreet || '',
            City: ag.CustomerCity || '',
            State: ag.CustomerState || '',
            PostalCode: ag.CustomerZip || '',
            Country: '',
            contacts: ag.CustomerContactName
              ? [
                  {
                    name: ag.CustomerContactName,
                    title: '',
                    location: '',
                    email: '',
                    phone: ag.CustomerPhone || '',
                    officePhone: '',
                    role: '',
                  },
                ]
              : [],
            plantAssignments: [],
          };
        }

        const events = [];
        for (const line of lines) {
          const { plantAssignments, event } = withProposalAssignment(
            supplier,
            line,
          );
          supplier = { ...supplier, plantAssignments };
          events.push(event);
        }
        if (isNew) await createSupplier(supplier);
        else await updateSupplier(supplier.id, supplier);

        const postedBy = name || email || '';
        for (const ev of events) {
          if (!ev) continue;
          try {
            // eslint-disable-next-line no-await-in-loop
            await logSupplierPlantAssignmentEvent({ ...ev, postedBy });
          } catch (logErr) {
            // eslint-disable-next-line no-console
            console.warn('[Sales] coverage event log failed', logErr);
          }
        }

        await updateAgreement(ag.id, {
          ...ag,
          SupplierCode: supplier.SupplierCode,
          ProposalStatus: PROPOSAL_STATUS.PROPOSAL_OUT,
          ProposalOutAt: new Date().toISOString(),
        });
        setAgreementError('');
        await Promise.all([agreements.refresh(), suppliers.refresh()]);
      } catch (err) {
        setAgreementError(`Couldn't send the proposal: ${err.message}`);
      }
    },
    [agreements, suppliers, nextSupplierCode, name, email],
  );

  const transitionProposal = useCallback(
    async (row, kind) => {
      try {
        const ag = row?.id ? await getAgreement(row.id) : row;
        const code = (ag.SupplierCode || '').toString().trim();
        if (!code) {
          setAgreementError(
            'This proposal isn’t linked to a customer yet. Use "Send / Mark Proposal Out" first.',
          );
          return;
        }
        if (
          !window.confirm(
            kind === 'won'
              ? 'Mark this proposal as won? Coverage goes Active and starts counting in the Sales Tracker.'
              : 'Mark this proposal as lost? Coverage will be closed.',
          )
        ) {
          return;
        }
        let supplier = await getSupplierByCode(code);
        if (!supplier) {
          setAgreementError(
            `Couldn't find the linked customer (code ${code}); it may have been deleted.`,
          );
          return;
        }
        const lines = linesFromAgreement(ag);
        const events = [];
        for (const line of lines) {
          const result =
            kind === 'won'
              ? winProposalAssignment(supplier, line.plantCode)
              : loseProposalAssignment(supplier, line.plantCode);
          supplier = { ...supplier, plantAssignments: result.plantAssignments };
          if (result.event) events.push(result.event);
        }
        await updateSupplier(supplier.id, supplier);

        const postedBy = name || email || '';
        for (const ev of events) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await logSupplierPlantAssignmentEvent({ ...ev, postedBy });
          } catch (logErr) {
            // eslint-disable-next-line no-console
            console.warn('[Sales] coverage event log failed', logErr);
          }
        }

        await updateAgreement(ag.id, {
          ...ag,
          ProposalStatus:
            kind === 'won' ? PROPOSAL_STATUS.WON : PROPOSAL_STATUS.LOST,
          ProposalClosedAt: new Date().toISOString(),
        });
        setAgreementError('');
        await Promise.all([agreements.refresh(), suppliers.refresh()]);
      } catch (err) {
        setAgreementError(`Couldn't update the proposal: ${err.message}`);
      }
    },
    [agreements, suppliers, name, email],
  );

  const onWonProposal = useCallback(
    (row) => transitionProposal(row, 'won'),
    [transitionProposal],
  );
  const onLostProposal = useCallback(
    (row) => transitionProposal(row, 'lost'),
    [transitionProposal],
  );

  // Expose current handlers + capability to the memoized agreement table.
  proposalActionsRef.current = {
    canManage: canManageProposals,
    onSendProposal,
    onWonProposal,
    onLostProposal,
  };

  return (
    <>
      <div className="page-header">
        <h1>Sales Manager</h1>
      </div>

      {/* ------- Sales Tracker ------- */}
      <section style={{ marginBottom: 32 }}>
        <p className="muted" style={{ marginTop: 0 }}>
          Coverage hours gained and lost as tracked on the{' '}
          <Link to="/suppliers">Suppliers</Link> page. Use the filters
          below to narrow by date range, plant, or supplier – totals
          and activity update live.
        </p>

        {/* Filter card — date-range presets + From/To date pickers on
            the top row, then Plant / Supplier / Regional Manager
            dropdowns underneath. Same shape as the Billing dashboard
            so muscle memory carries over. Each filter feeds the
            filteredTrackerEvents memo, which is what every downstream
            tile / table reads. */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <SalesPresetButton
              label="This Month"
              onClick={() => applyPreset('this-month')}
              active={activePreset === 'this-month'}
            />
            <SalesPresetButton
              label="Last Month"
              onClick={() => applyPreset('last-month')}
              active={activePreset === 'last-month'}
            />
            <SalesPresetButton
              label="This Week"
              onClick={() => applyPreset('this-week')}
              active={activePreset === 'this-week'}
            />
            <SalesPresetButton
              label="Last Week"
              onClick={() => applyPreset('last-week')}
              active={activePreset === 'last-week'}
            />
            <SalesPresetButton
              label="Past 4 Weeks"
              onClick={() => applyPreset('past-4-weeks')}
              active={activePreset === 'past-4-weeks'}
            />
            <SalesPresetButton
              label="Past 5 Weeks"
              onClick={() => applyPreset('past-5-weeks')}
              active={activePreset === 'past-5-weeks'}
            />
            <SalesPresetButton
              label="Year to Date"
              onClick={() => applyPreset('ytd')}
              active={activePreset === 'ytd'}
            />
            <SalesPresetButton
              label="Last Year"
              onClick={() => applyPreset('last-year')}
              active={activePreset === 'last-year'}
            />
          </div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--brand-muted)',
                }}
              >
                From
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset(null);
                }}
                style={{
                  padding: '8px 10px',
                  border: '1px solid var(--brand-border)',
                  borderRadius: 6,
                  fontSize: 14,
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--brand-muted)',
                }}
              >
                To
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset(null);
                }}
                style={{
                  padding: '8px 10px',
                  border: '1px solid var(--brand-border)',
                  borderRadius: 6,
                  fontSize: 14,
                }}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginTop: 12,
              borderTop: '1px solid var(--brand-border, #e5e7eb)',
              paddingTop: 12,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--brand-muted)',
                }}
              >
                Plant
              </label>
              <select
                value={plantFilter}
                onChange={(e) => setPlantFilter(e.target.value)}
                style={{
                  padding: '8px 10px',
                  border: '1px solid var(--brand-border)',
                  borderRadius: 6,
                  fontSize: 14,
                  minWidth: 240,
                }}
              >
                <option value="">All plants</option>
                {(plants.data || [])
                  .filter((p) => p.PlantCode)
                  .slice()
                  .sort((a, b) =>
                    String(a.PlantCode).localeCompare(String(b.PlantCode)),
                  )
                  .map((p) => (
                    <option key={p.PlantCode} value={p.PlantCode}>
                      {p.Title ? `${p.PlantCode} — ${p.Title}` : p.PlantCode}
                    </option>
                  ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--brand-muted)',
                }}
              >
                Supplier
              </label>
              <select
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
                style={{
                  padding: '8px 10px',
                  border: '1px solid var(--brand-border)',
                  borderRadius: 6,
                  fontSize: 14,
                  minWidth: 240,
                }}
              >
                <option value="">All suppliers</option>
                {(suppliers.data || [])
                  .filter((s) => s.SupplierCode)
                  .slice()
                  .sort((a, b) => {
                    const an = (a.Title || a.SupplierCode || '').toLowerCase();
                    const bn = (b.Title || b.SupplierCode || '').toLowerCase();
                    return an.localeCompare(bn);
                  })
                  .map((s) => (
                    <option key={s.SupplierCode} value={s.SupplierCode}>
                      {s.Title
                        ? `${s.SupplierCode} — ${s.Title}`
                        : s.SupplierCode}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        {/* Headline stat cards — three compact tiles that sum across every
            logged event. Kept lightweight (no custom component) so the
            card lines up visually with the stat tiles on Dashboard. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
              Hours Added per Day
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 600,
                color: 'var(--brand-success, #16a34a)',
              }}
            >
              +{trackerTotals.added}h
            </div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
              Hours Lost per Day
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 600,
                color: 'var(--brand-danger)',
              }}
            >
              −{trackerTotals.lost}h
            </div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
              Net Change per Day
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 600,
                color:
                  trackerTotals.net >= 0
                    ? 'var(--brand-success, #16a34a)'
                    : 'var(--brand-danger)',
              }}
            >
              {trackerTotals.net >= 0 ? '+' : '−'}
              {Math.abs(trackerTotals.net)}h
            </div>
          </div>
        </div>

        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Activity</h3>

        {trackerEvents.loading && <Loading />}
        {trackerEvents.error && (
          <div
            className="card"
            style={{ borderColor: 'var(--brand-danger)' }}
          >
            <div className="card-title">Couldn't Load Sales Manager</div>
            <p>{trackerEvents.error.message}</p>
            <button
              className="btn btn-outline btn-sm"
              onClick={trackerEvents.refresh}
            >
              Retry
            </button>
          </div>
        )}
        {!trackerEvents.loading && !trackerEvents.error && (
          <DataTable
            rows={filteredTrackerEvents}
            columns={trackerColumns}
            emptyMessage="No coverage changes match the current filters. Adjust the date range or filters to see more, or change a supplier's per-plant Coverage Hours to log a new event."
          />
        )}
      </section>

      {/* ------- Pre-Launch Plants ------- */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ margin: '0 0 8px' }}>Pre-Launch Plants</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          OEM plants with vehicles currently in Pre-Launch.
        </p>
        <DataTable
          rows={prelaunchPlants}
          columns={prelaunchColumns}
          onRowClick={(row) =>
            row.PlantCode && navigate(`/plants/${encodeURIComponent(row.PlantCode)}`)
          }
          emptyMessage="No plants are in Pre-Launch right now."
        />
      </section>

      {/* ------- Sales Leads ------- */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ margin: '0 0 8px' }}>Sales Leads</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Suppliers we’re currently engaging.
        </p>
        <DataTable
          rows={prospectingRows}
          columns={prospectingColumns}
          onRowClick={(row) =>
            row.supplierCode &&
            navigate(`/suppliers/${encodeURIComponent(row.supplierCode)}`)
          }
          subRender={renderSupplierNotesSubRow}
          emptyMessage="No suppliers are in Prospecting right now."
        />
      </section>

      {/* ------- Pending Leads ------- */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ margin: '0 0 8px' }}>Pending Leads</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Suppliers with a proposal out, awaiting response.
        </p>
        <DataTable
          rows={proposalOutRows}
          columns={prospectingColumns}
          onRowClick={(row) =>
            row.supplierCode &&
            navigate(`/suppliers/${encodeURIComponent(row.supplierCode)}`)
          }
          subRender={renderSupplierNotesSubRow}
          emptyMessage="No suppliers have a proposal out right now."
        />
      </section>

      {/* ------- Sales Proposals ------- */}
      <section style={{ marginBottom: 32 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <h2 style={{ margin: 0 }}>Sales Proposals</h2>
          {canManageProposals && (
            <button className="btn btn-primary" onClick={onNewAgreement}>
              + New Proposal
            </button>
          )}
        </div>

        {agreementError && (
          <div
            className="card"
            style={{ borderColor: 'var(--brand-danger)', marginBottom: 12 }}
          >
            <p>{agreementError}</p>
          </div>
        )}

        {agreements.loading && <Loading />}
        {agreements.error && (
          <div
            className="card"
            style={{ borderColor: 'var(--brand-danger)' }}
          >
            <div className="card-title">Couldn't Load Proposals</div>
            <p>{agreements.error.message}</p>
            <button
              className="btn btn-outline btn-sm"
              onClick={agreements.refresh}
            >
              Retry
            </button>
          </div>
        )}
        {!agreements.loading && !agreements.error && (
          agreementGroups.length === 0 ? (
            // Empty-state fallback — DataTable handles its own empty state
            // but we render groups, so we need to show something ourselves
            // when there are no rows at all.
            <div className="card">
              <p className="muted" style={{ margin: 0 }}>
                No proposals yet. Click “+ New Proposal” to generate your
                first proposal.
              </p>
            </div>
          ) : (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              {agreementGroups.map(([year, rows], index) => {
                const expanded = isYearExpanded(year, index);
                return (
                  <div key={year}>
                    {/* Year header — acts as the collapse toggle. Full-width
                        clickable bar so the hit target is comfortable and
                        the chevron + count are visible at a glance. */}
                    <button
                      type="button"
                      onClick={() => toggleYear(year)}
                      aria-expanded={expanded}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 14px',
                        background: 'var(--brand-surface, #f8fafc)',
                        border: '1px solid var(--brand-border)',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'var(--brand-ink)',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 10,
                            transition: 'transform 0.15s ease',
                            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        >
                          ▶
                        </span>
                        {year}
                      </span>
                      <span style={{ color: 'var(--brand-muted)', fontWeight: 400 }}>
                        {rows.length} {rows.length === 1 ? 'proposal' : 'proposals'}
                      </span>
                    </button>
                    {/* Body — only rendered when expanded so collapsed
                        years don't pay the DataTable render cost. */}
                    {expanded && (
                      <div style={{ marginTop: 8 }}>
                        <DataTable
                          rows={rows}
                          columns={agreementColumns}
                          onRowClick={canManageProposals ? onEditAgreement : undefined}
                          emptyMessage=""
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </section>

      {/* ------- Sales Resources ------- */}
      <section>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <h2 style={{ margin: 0 }}>Sales Resources</h2>
          {isDirector && (
            <button className="btn btn-primary" onClick={onAddResource}>
              + Upload Resource
            </button>
          )}
        </div>

        {resources.loading && <Loading />}
        {resources.error && (
          <div
            className="card"
            style={{ borderColor: 'var(--brand-danger)' }}
          >
            <div className="card-title">Couldn't Load Resources</div>
            <p>{resources.error.message}</p>
            <button
              className="btn btn-outline btn-sm"
              onClick={resources.refresh}
            >
              Retry
            </button>
          </div>
        )}

        {!resources.loading && !resources.error && (
          <ResourcesGrid
            resources={resources.data || []}
            canManage={isDirector}
            onRemove={onRemoveResource}
            emptyMessage={
              isDirector
                ? 'No sales resources yet. Click “+ Upload Resource” to add a one-pager, deck, or case study.'
                : 'No sales resources yet.'
            }
          />
        )}
      </section>

      {/* ------- Modals ------- */}
      <Modal
        open={creating || !!editing}
        onClose={closeAgreementModal}
        title={
          editing
            ? editing.CustomerCompany
              ? `Edit Proposal — ${editing.CustomerCompany}`
              : 'Edit Proposal'
            : 'New Proposal'
        }
      >
        {(creating || editing) && (
          <AgreementForm
            // `initial` seeds the form — an empty object in create mode,
            // the full decoded row in edit mode. AgreementForm rebuilds
            // its internal state when initial?.id changes.
            initial={editing || {}}
            suppliers={suppliers.data || []}
            plants={plants.data || []}
            saving={saving}
            deleting={deleting}
            // onDelete is only wired when editing, so the form shows
            // the delete button in edit mode only.
            onDelete={editing ? onDeleteAgreement : undefined}
            onSave={onSaveAgreement}
            onCancel={closeAgreementModal}
          />
        )}
      </Modal>

      <Modal
        open={addingResource}
        onClose={() => {
          if (resourceSaving) return;
          setResourceError('');
          setAddingResource(false);
        }}
        title="Upload Sales Resource"
      >
        {addingResource && (
          <ResourceForm
            saving={resourceSaving}
            error={resourceError}
            onSave={onSaveResource}
            onCancel={() => {
              setResourceError('');
              setAddingResource(false);
            }}
          />
        )}
      </Modal>
    </>
  );
}

/**
 * Sub-row renderer for the Prospecting / Proposal Out supplier tables
 * on the Sales dashboard. When a row has notes attached to its
 * supplier-plant assignment, render them in a subtle "Notes" block
 * directly under the main row. Returns null when no notes — the
 * DataTable then skips the sub-row entirely so rows without notes
 * stay tight.
 */
// Toggleable date-range preset chip used by the Sales Tracker filter
// card. Active state mirrors the Billing dashboard treatment — brand
// orange primary button when selected, neutral outline otherwise — so
// the active range reads at a glance and matches the equivalent
// control on /billing.
function SalesPresetButton({ label, onClick, active = false }) {
  return (
    <button
      type="button"
      className={active ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
      aria-pressed={active}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function renderSupplierNotesSubRow(row) {
  const text = (row.notes || '').trim();
  if (!text) return null;
  return (
    <div
      style={{
        padding: '8px 12px',
        background: '#f8fafc',
        borderLeft: '3px solid var(--brand-primary, #E87722)',
        borderRadius: 4,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'var(--brand-muted)',
          marginBottom: 4,
        }}
      >
        Notes
      </div>
      <div
        style={{
          fontSize: 13,
          whiteSpace: 'pre-wrap',
          color: 'var(--brand-ink)',
        }}
      >
        {text}
      </div>
    </div>
  );
}

/**
 * Vehicle name labels for the Pre-Launch column on the Sales
 * dashboard. Scoped to vehicles whose status is Pre-Launch — a plant
 * could have an unrelated Production vehicle that doesn't belong on
 * the upcoming-pipeline view. Falls back to the legacy single-string
 * `name` when newer make/model aren't filled in. Deduped + sorted.
 */
function prelaunchVehicleLabels(plant) {
  const seen = new Set();
  const out = [];
  for (const v of plant.vehicles || []) {
    if ((v?.status || '').trim() !== 'Pre-Launch') continue;
    const label =
      v.model ||
      v.name ||
      [v.make, v.model].filter(Boolean).join(' ').trim();
    const trimmed = (label || '').trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

/**
 * Program labels for the Pre-Launch column on the Sales dashboard.
 * Same Pre-Launch scoping as prelaunchVehicleLabels — only programs
 * tied to a Pre-Launch vehicle qualify. Falls back to the legacy
 * `modelYear` key for plants saved before the rename.
 */
function prelaunchProgramLabels(plant) {
  const seen = new Set();
  const out = [];
  for (const v of plant.vehicles || []) {
    if ((v?.status || '').trim() !== 'Pre-Launch') continue;
    const raw = (v?.program || v?.modelYear || '').trim();
    if (!raw || seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

